import { Check, X } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { bucketForStatus } from "../../utils/cryptoSwapStatus";

export type TimelineStepState = "done" | "current" | "todo" | "failed";

export interface TimelineStep {
  id: string;
  label: string;
  hint?: ReactNode;
  state: TimelineStepState;
}

export interface OrderTimelineProps {
  order: OtcOrder;
}

/** Created → processing → delivered or failed, derived from the status and the order's own timestamps. */
export function OrderTimeline({ order }: OrderTimelineProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const bucket = bucketForStatus(order.status);
  const failed = bucket === "failed";
  const paid = bucket === "done";
  const processing = order.status === "PROCESSING";
  const when = (value: string | null) =>
    value ? formatDateTime(value, { locale: activeLanguage }) : undefined;

  const steps: TimelineStep[] = [
    { id: "created", label: t("timeline_created"), hint: when(order.created_at), state: "done" },
    {
      id: "processing",
      label: t("timeline_processing"),
      hint: processing ? t("timeline_processing_hint") : undefined,
      state: processing ? "current" : paid || failed ? "done" : "todo",
    },
    failed
      ? { id: "failed", label: t("timeline_failed"), hint: when(order.failed_at), state: "failed" }
      : {
          id: "delivered",
          label: t("timeline_delivered"),
          hint: when(order.paid_at),
          state: paid ? "done" : "todo",
        },
  ];

  return <TimelineSteps steps={steps} />;
}

/** The tracking card both directions draw: one marker per step, the line solid up to the last one done. */
export function TimelineSteps({ steps }: { steps: TimelineStep[] }) {
  const { t } = useTranslations("cryptoSwaps");

  return (
    <ColocaSurface className="flex flex-col gap-4 rounded-2xl">
      <h2 className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        {t("timeline_title")}
      </h2>
      <ol className="m-0 flex list-none flex-col p-0">
        {steps.map((step, index) => (
          <li
            key={step.id}
            aria-current={step.state === "current" ? "step" : undefined}
            className="flex gap-3.5"
          >
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "box-border flex size-5.5 shrink-0 items-center justify-center rounded-full border-2",
                  step.state === "done" && "border-foreground bg-foreground text-background",
                  step.state === "current" && "border-warning",
                  step.state === "todo" && "border-border",
                  step.state === "failed" && "border-destructive bg-destructive text-background",
                )}
              >
                {step.state === "done" && <Check aria-hidden="true" className="size-3" strokeWidth={3} />}
                {step.state === "failed" && <X aria-hidden="true" className="size-3" strokeWidth={3} />}
                {step.state === "current" && (
                  <span aria-hidden="true" className="size-2 animate-pulse rounded-full bg-warning" />
                )}
              </span>
              {index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "min-h-6 w-0.5 flex-1",
                    step.state === "done" ? "bg-foreground" : "bg-border",
                  )}
                />
              )}
            </div>
            <div className={cn(index < steps.length - 1 && "pb-4")}>
              <p
                className={cn(
                  "text-sm",
                  step.state === "todo" ? "text-muted-foreground" : "font-semibold text-foreground",
                )}
              >
                {step.label}
                <span className="sr-only">
                  {" — "}
                  {step.state === "done"
                    ? t("timeline_state_done")
                    : step.state === "current"
                      ? t("timeline_state_current")
                      : step.state === "failed"
                        ? t("timeline_state_failed")
                        : t("timeline_state_todo")}
                </span>
              </p>
              {step.hint && <div className="text-xs text-muted-foreground">{step.hint}</div>}
            </div>
          </li>
        ))}
      </ol>
    </ColocaSurface>
  );
}
